import React, { useState, useEffect } from "react";
import { Box, Typography, Tab, useTheme } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import InformationStepsCard from "../../components/InformationStepsCard";
import VendorRegisterTab from "./Overview/VendorRegisterTab";
import DiscoveredVendorsTab from "./Discovered/DiscoveredVendorsTab";
import MonitoringFeed from "./Monitoring/MonitoringFeed";
import useVendorStore from "./store/useVendorStore";
import useVendorStaff from "./hooks/useVendorStaff";
import { styles } from "./styles/Vendors.styles";

const TAB_TITLES = {
  "0": "Vendor Register",
  "1": "Discovered Vendors",
  "2": "Continuous Monitoring",
};

const VendorMain = () => {
  const theme = useTheme();
  const [tab, setTab] = useState("0");
  const { people } = useVendorStaff();
  const assignOwnersFromStaff = useVendorStore((s) => s.assignOwnersFromStaff);
  const discoveredCount = useVendorStore((s) => s.discovered.length);
  const openAlerts = useVendorStore(
    (s) => s.monitoring.filter((m) => !m.acknowledged).length
  );

  useEffect(() => {
    if (people.length) assignOwnersFromStaff(people);
  }, [people, assignOwnersFromStaff]);

  return (
    <Box sx={styles.pageContainer}>
      <TabContext value={tab}>
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="h5"
            fontWeight="600"
            color={theme.customColors.text.strong}
            sx={{ mb: 0.5 }}
          >
            {TAB_TITLES[tab]}
          </Typography>
        </Box>

        <Box sx={{ mb: 3, mt: 3 }}>
          <InformationStepsCard
            heading="**What should I do on this page?**"
            subtitle="This page helps you manage third-party vendors, assess their security risk, and monitor changes over time."
            steps={[
              "**Vendor Register** tab: View and manage all vendors. Add vendors, assign owners, and track inherent risk and review status.",
              "**Discovered** tab: Review vendors auto-detected from Google Workspace and add the ones your organization uses.",
              "Open a vendor to set **inherent risk**, run a **security review**, and record a recommendation.",
              "**Monitoring** tab: Review breaches, vulnerabilities, and posture changes across your vendor portfolio.",
            ]}
            footerText="A structured vendor review program keeps you audit-ready and reduces third-party risk exposure."
            collapsible={true}
            defaultExpanded={false}
          />
        </Box>

        <Box
          sx={{
            borderBottom: 1,
            borderColor: theme.customColors.border.main,
            mb: 2,
          }}
        >
          <TabList onChange={(_, v) => setTab(v)} aria-label="Vendor Management Tabs">
            <Tab label="Vendor Register" value="0" />
            <Tab
              label={discoveredCount > 0 ? `Discovered (${discoveredCount})` : "Discovered"}
              value="1"
            />
            <Tab
              label={openAlerts > 0 ? `Monitoring (${openAlerts})` : "Monitoring"}
              value="2"
            />
          </TabList>
        </Box>

        <TabPanel value="0" sx={{ p: 0 }}>
          <VendorRegisterTab onGoToDiscovered={() => setTab("1")} people={people} />
        </TabPanel>
        <TabPanel value="1" sx={{ p: 0 }}>
          <DiscoveredVendorsTab />
        </TabPanel>
        <TabPanel value="2" sx={{ p: 0 }}>
          <MonitoringFeed />
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export default VendorMain;
